import { useCallback, useMemo, useState } from 'react';

import { Enum } from '~/data/enums';
import { BillableHoursProposalProps } from '~/data/proposals';
import { BillableHoursTypeConfig } from '~/data/rewarding/types';
import { BillableHoursTypeConfigSelectorProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/components/BillableHoursTypeConfigSelector';
import { billableHoursTypeConfigUtils } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/data';

type PropsType = {
  proposal: BillableHoursProposalProps;
  billableHoursTypeConfigs: BillableHoursTypeConfig[];
  currency: string;
  billableTypes: Enum[];
};

export const useBillableHoursTypeConfig = (props: PropsType) => {
  const { proposal, billableHoursTypeConfigs, currency, billableTypes } = props;
  const { proposalData } = proposal;

  // Utils
  const { getNonPresentHoursTypeConfig, findConfigById, getDefaultConfigs } =
    billableHoursTypeConfigUtils();

  const {
    defaultTypeConfigOptions,
    defaultBillableHoursTypeConfig,
    defaultBillableType,
  } = useMemo(() => {
    const { options, selected } = getDefaultConfigs({
      typeConfigs: billableHoursTypeConfigs,
      proposalHoursType: proposalData?.billableHoursTypeFK,
    });

    return {
      defaultTypeConfigOptions: options,
      defaultBillableHoursTypeConfig: selected,
      defaultBillableType: selected?.fBillableHoursType,
    };
  }, [getDefaultConfigs, billableHoursTypeConfigs, proposalData]);

  // Ensure the available options don't change after the form is initialized
  // to prevent synchronization issues between config selector & form data
  const [hoursTypeConfigOptions] = useState<BillableHoursTypeConfig[]>(
    defaultTypeConfigOptions,
  );

  const [selectedHoursTypeConfig, setSelectedHoursTypeConfig] = useState<
    BillableHoursTypeConfig | undefined
  >(defaultBillableHoursTypeConfig);

  const onSelectedHoursTypeConfigChange = useCallback(
    (
      typeConfig: BillableHoursTypeConfig | undefined,
    ): Promise<BillableHoursTypeConfig | undefined> => {
      return new Promise((resolve, reject) => {
        const foundConfig = findConfigById(
          hoursTypeConfigOptions,
          typeConfig?.fEmplSubUId ?? '',
        );

        if (!foundConfig) {
          reject('optionNotPresent');
        } else {
          setSelectedHoursTypeConfig(foundConfig);
          resolve(foundConfig);
        }
      });
    },
    [hoursTypeConfigOptions, setSelectedHoursTypeConfig, findConfigById],
  );

  const billableHoursTypeConfigSelector: BillableHoursTypeConfigSelectorProps =
    {
      getNonPresentHoursTypeConfig,
      selectedHoursTypeConfig,
      onSelectedHoursTypeConfigChange,
      options: hoursTypeConfigOptions,
      typeEnums: billableTypes,
      currency,
    };

  return {
    defaultBillableType,
    billableHoursTypeConfigSelector,
    selectedHoursTypeConfig,
  };
};
